/**
 * Aide contextuelle pour le module Signalement de bugs portail.
 *
 * Routes couvertes :
 * - /admin/bug-reports : liste des signalements
 * - /admin/bug-reports/:id : détail d'un signalement
 */

const bugReportsHelp = {
  '/admin/bug-reports': {
    title: 'Bugs portail',
    user: [
      {
        heading: 'À quoi sert cette page ?',
        content:
          'Centralise les **bugs** signalés (bouton "Signaler un bug") ET les **idées / améliorations** proposées (bouton "Proposer une idée") via le panneau d\'aide (icône `?` flottante). Les onglets **Tous / Bugs / Idées** filtrent par type. Une idée est estampillée 💡 et affichée avec le vocabulaire « idée » (Nouvelle, À l\'étude, En développement, Réalisée).',
      },
      {
        heading: 'Workflow',
        content:
          'new → triaged (prêt pour Claude) → in_claude → fixed → closed.\n\nLes statuts terminaux sont closed, wontfix et duplicate. Une transition new → fixed direct est interdite (passer par triaged puis in_claude).',
      },
      {
        heading: 'Créer un bug depuis un email (glisser-déposer)',
        content:
          'Glissez un email (`.eml` / `.msg`) **n\'importe où sur cette page** : il devient un signalement. Le sujet fait le titre, le corps la description, et la catégorie/priorité sont devinées depuis les mots-clés (à corriger d\'un clic si besoin). Sur l\'onglet **Idées**, le mail déposé crée une idée au lieu d\'un bug.\n\nUtile pour les alertes reçues par mail (tâche planifiée en échec, sauvegarde KO, erreur applicative) : plutôt que de les laisser filer, on en fait un signalement traçable.',
      },
      {
        heading: 'Une alerte qui revient ne crée PAS de doublon',
        content:
          'Le backend calcule une **signature** (expéditeur + sujet dont on retire l\'heure, la date et les identifiants de run). Un second dépôt de la même alerte incrémente le **compteur d\'occurrences** du bug existant — visible en badge `×N` dans la liste, avec l\'historique daté dans la fiche.\n\nSi le bug était `fixed`, un nouveau dépôt le **rouvre** : le correctif n\'a pas tenu.\n\nL\'email d\'origine est déplacé vers le dossier **Tickets traités** de la boîte réceptrice, comme pour un ticket, et le `.eml` est joint au signalement (téléchargeable depuis sa fiche).',
      },
      {
        heading: 'Bouton "Copier le prompt" (traitement par lot)',
        content:
          'En haut de la liste : copie un prompt **prêt à coller dans Claude Code** pour traiter d\'un coup tous les signalements en attente (numéro, titre, identifiant, récurrence, marche à suivre). Le portail prépare le travail ; le lancement reste ton geste.\n\nLe lot suit l\'onglet de statut ouvert : sur **À traiter** il embarque les signalements prêts pour analyse, sur **Nouveaux** ceux qui arrivent (dont les bugs posés automatiquement par le crawler). Lot plafonné à 25 — le toast le signale, relancer après traitement.\n\nLe prompt interdit explicitement le correctif de complaisance : si la cause est hors code (réseau, pare-feu, configuration d\'un tiers), Claude doit documenter le diagnostic au lieu de « réparer » quelque chose.',
      },
      {
        heading: 'Rappel par mail : le briefing de 7 h',
        content:
          'La cloche du portail se compte en centaines de non-lues : elle ne sert pas d\'alerte. Le **briefing matinal de 7 h** porte donc une section Signalements — ce qui est prêt pour analyse, ce qui traîne depuis plus de 7 jours (nommé un par un), et le nombre de bugs crawler à trier. Chaque ligne renvoie vers la liste filtrée, d\'où le prompt se copie.\n\nUn signalement de priorité **critique** ne l\'attend pas : il déclenche un mail immédiat.',
      },
      {
        heading: 'Bouton "Marquer prêt pour Claude"',
        content:
          'Quand un bug est suffisamment décrit et catégorisé, cliquer ce bouton pour le faire passer en `triaged`. Le payload markdown devient disponible via l\'endpoint `/api/bug-reports/{id}/claude-payload` que Claude Code peut récupérer pour analyse.',
      },
    ],
    admin: [
      {
        heading: 'Endpoint Claude bridge',
        content:
          '`GET /api/bug-reports/{id}/claude-payload` retourne un markdown structuré (URL, route, console errors, network failures, état app, screenshot URL absolue). `PATCH /api/bug-reports/{id}/claude-resolution` permet à Claude de poser branche + commit_hash + résumé après fix.',
      },
      {
        heading: 'Identité JWT',
        content:
          'Les 2 endpoints Claude bridge exigent l\'identité `assistant-ia@meteris.fr` (rôle superadmin). Refus explicite pour TOUS les autres acteurs, y compris un superadmin humain. Toute tentative refusée est loguée dans `col_activity` (action `claude_endpoint_refused`).',
      },
      {
        heading: 'Notification au reporter',
        content:
          'Quand un bug passe en `fixed` (via PATCH admin OU claude-resolution), une notification `bug_report_fixed` est insérée dans `col_notifications` ciblée sur `reporter.user_id`. Severity `info`, priority `low`, category `bug_reports`.',
      },
      {
        heading: 'Dépôt d\'email : endpoints',
        content:
          '`POST /api/bug-reports/create-from-email` (multipart `file` + `kind`) — gate `admin.bug_reports`. Parse mutualisé avec le drop ticket/CRM (`email_drop_parser_svc`), dédup dans `bug_email_svc.report_email_bug` (`source: email_drop`, champ `signature`, collection `col_bug_report_occurrences`).\n\n`GET /api/bug-reports/{id}/occurrences` — historique des réoccurrences (sert aussi les bugs crawler). `GET /api/bug-reports/{id}/email` — télécharge le `.eml` source (GridFS, stocké à la création uniquement).',
      },
      {
        heading: 'Compteur "Bug portail #N"',
        content:
          'Séquence atomique stockée dans `col_settings._id=bug_report_counter`. Seedé à `next:4` par `scripts/seed_bug_report_counter.py` pour démarrer à #5 (continuité historique : les Bug #1-4 restent dans le ticket #18846).',
      },
      {
        heading: 'Bugs vs Idées (même module)',
        content:
          'Bugs et idées partagent la collection `col_bug_reports`, distingués par le champ `kind` (`bug` | `feature`). Les idées ont un compteur séparé (`col_settings._id=feature_request_counter`, Idée #1, #2…), pas de capture console/réseau, et deux axes de classement : `zone` + `nature`. Le triage (priorité = criticité, notes = avis, transitions de statut) et le pont Claude sont identiques.',
      },
    ],
  },
  '/admin/bug-reports/:id': {
    title: 'Détail bug portail',
    user: [
      {
        heading: 'Capture automatique',
        content:
          'Au moment du signalement, le navigateur a capturé : URL, route React, user-agent, viewport, console errors (20 dernières), requêtes réseau échouées (10 dernières), état app (localStorage safelisté) et screenshot DOM via html2canvas. Tout est consultable dans les sections collapsibles.',
      },
      {
        heading: 'Triage rapide',
        content:
          'Côté droit : changer statut, priorité (= criticité) et ajouter des notes admin (= ton avis, autosave au blur). Pour un **bug** on classe par catégorie ; pour une **idée** on voit Zone + Nature (renseignées par l\'auteur) et les statuts utilisent le vocabulaire idée (Nouvelle → À l\'étude → En développement → Réalisée). Le bouton **"Marquer prêt pour Claude"** apparaît si le statut est `new`.',
      },
      {
        heading: 'Résolution Claude',
        content:
          'Si Claude a déjà traité ce bug, le bloc vert "Résolution Claude" affiche la branche, le commit (lien GitHub), la date de déploiement et le résumé en markdown.',
      },
    ],
    admin: [
      {
        heading: 'Auto-save notes',
        content:
          'Les notes admin sont enregistrées au blur du textarea (pattern debounce léger). Évite les pertes accidentelles tout en limitant les requêtes.',
      },
      {
        heading: 'Validation des transitions',
        content:
          'Le backend valide les transitions de statut via `ALLOWED_TRANSITIONS`. Une transition invalide (ex: `closed → fixed`) retourne 400. Le frontend affiche un toast d\'erreur.',
      },
    ],
  },
};

export default bugReportsHelp;
